import { useQueryClient } from "@tanstack/react-query";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { getIn, useFormik } from "formik";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import PhoneInput from "react-phone-input-2";
import { v4 as uuidv4 } from "uuid";
import * as yup from "yup";

import {
  ICustomFieldAnswer,
  ICustomFieldError,
  ICustomFieldFormItem,
} from "../../../../@types/CustomField";
import { IEnrolemntAddDevice } from "../../../../@types/Devices";
import {
  IOnboardingFieldSetting,
  IWardConfiguration,
} from "../../../../@types/Login";
import { Notification } from "../../../../@types/Notification";
import {
  IPortalPreferences,
  IUserPreferences,
} from "../../../../@types/Preferences";
import { Close } from "../../../../assets/icons/Close";
import { Button } from "../../../../components/Button";
import { DateTimePickerInput } from "../../../../components/DateTimePicker";
import { IOption, SelectDropdown } from "../../../../components/SelectDropdown";
import { SkeletonLoading } from "../../../../components/Skeleton";
import { SnomedSelect } from "../../../../components/SnomedSelect";
import { TextField } from "../../../../components/TextField";
import {
  AUTH_PORTAL_PREFERENCES,
  AUTH_UNIT_PREFERENCES,
  AUTH_WARD_CONFIGURATION,
} from "../../../../constants/localStorageKeys";
import { searchAllergyOptions } from "../../../../hooks/queries/allergies";
import { useCustomFieldsForWards } from "../../../../hooks/queries/customFields";
import { searchDiagnosisOptions } from "../../../../hooks/queries/diagnoses";
import { PATIENT_SEARCH_LIST_QUERY_KEY } from "../../../../hooks/queries/patients";
import { useStandardFieldsForWards } from "../../../../hooks/queries/standardFields";
import { useAvailableWards } from "../../../../hooks/queries/wards";
import { useArrowsListennerHandler } from "../../../../hooks/useArrowsListennerHandler";
import { useWardLabel } from "../../../../hooks/useWardLabel";
import { devicesService } from "../../../../services/devicesService";
import { patientService } from "../../../../services/patientService";
import {
  buildCustomFieldsPayload,
  CustomFieldAnswers,
  CustomFieldErrors,
  mapCustomFieldErrorsByLabel,
  validateCustomFields,
} from "../../../../utils/customFields";
import {
  getEnabledOnboardingFields,
  isOnboardingFieldEnabled,
  isOnboardingFieldRequired,
} from "../../../../utils/onboardingFields";
import {
  DATE_VALUE_FORMAT,
  getDateFormatForCountry,
} from "../../../../utils/portalDateFormat";
import {
  isStandardFieldEnabled,
  isStandardFieldRequired,
  requiredMarker,
} from "../../../../utils/standardFields";
import { safeStorage, safeJsonParse } from "../../../../utils/storage";
import { isWardFeatureEnabledForPatient } from "../../../../utils/wardConfig";
import { Title } from "../../../PatientPage/components/AddMeasurementModal/AddMeasurementModal.styles";
import { CancelButton } from "../../../Settings/components/SecurityModal/SecurityModal.styles";
import { AdditionalDetailsSection } from "./AdditionalDetailsSection";
import {
  dropdownCss,
  ErrorMessage,
  FormField,
  ModalContainer,
  inputCss,
  reactPhoneInputCss,
  SectionContainer,
  SubmitContainer,
  dropdownPopUpCss,
  EnrolmentContainer,
  AddDeviceButton,
  CloseContainer,
} from "./AddPatientModal.styles";
import { getDateOfBirthError, toDateOfBirthPayload } from "./dateOfBirth";
import "react-phone-input-2/lib/bootstrap.css";

dayjs.extend(utc);

type AddPatientProps = {
  onClose(notification?: Notification): void;
};

export function AddPatientModal({ onClose }: AddPatientProps) {
  const { t } = useTranslation();
  const wardLabel = useWardLabel();
  const queryClient = useQueryClient();
  const { availableWards, isWardsFetching, isWardsError } = useAvailableWards();
  const { disablePatientListArrowNav } = useArrowsListennerHandler();

  const [enabledOnboardingFields, setEnabledOnboardingFields] = useState<
    IOnboardingFieldSetting[]
  >([]);

  const onboardingGates = useMemo(
    () => ({
      allergies: isOnboardingFieldEnabled("Allergies", enabledOnboardingFields),
      diagnosis: isOnboardingFieldEnabled("Diagnosis", enabledOnboardingFields),
    }),
    [enabledOnboardingFields]
  );

  const onboardingRequired = useMemo(
    () => ({
      allergies: isOnboardingFieldRequired(
        "Allergies",
        enabledOnboardingFields
      ),
      diagnosis: isOnboardingFieldRequired(
        "Diagnosis",
        enabledOnboardingFields
      ),
    }),
    [enabledOnboardingFields]
  );

  const [sexSelected, setSexSelected] = useState<string>();
  const [ethnicitySelected, setEthnicitySelected] = useState<string>();
  const [allergiesSelected, setAllergiesSelected] = useState<IOption[]>([]);
  const [diagnosesSelected, setDiagnosesSelected] = useState<IOption[]>([]);
  const [phoneWithCountryCode, setPhoneWithCountryCode] = useState<string>("");

  const [wardOptions] = useState<IOption[]>([]);
  // EN-4960: a patient can belong to multiple wards. The FIRST selected ward is the primary.
  // The selected wards live in `formik.values.ward` (single source of truth).
  const [showWards, setShowWards] = useState<boolean>(false);

  const [countryCode, setCountryCode] = useState<string>();
  const [heightUnit, setHeightUnit] = useState<string>();
  const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false);
  const [responseError, setResponseError] = useState<string>("");

  // EN-5067: dynamic per-ward custom fields. Inline errors keyed by definition id, plus the ids of
  // fields that flipped from optional to required after a ward was added this session.
  const [customFieldErrors, setCustomFieldErrors] = useState<CustomFieldErrors>(
    {}
  );
  const [newlyRequiredIds, setNewlyRequiredIds] = useState<string[]>([]);

  const [hasNoAppEnrolment, setHasNoAppEnrolment] = useState<boolean>(false);
  const [hasEmailValue, setHasEmailValue] = useState<string>();

  // EN-5192: the wards whose standard-field configuration the form is currently honouring.
  // Kept alongside formik.values.ward (the source of truth for the selection) because the yup
  // schema is built before formik exists and has to react to the ward set changing.
  const [wardIdsForFieldConfig, setWardIdsForFieldConfig] = useState<number[]>(
    []
  );

  const [canAddDevice, setCanAddDevice] = useState<boolean>(false);
  const [deviceTypesOptions, setDeviceTypesOptions] = useState<IOption[]>([]);

  const isSubmittingRef = useRef(false);
  // EN-5067: the currently-shown custom fields, mirrored into a ref so handleOnSubmitForm (defined
  // before the fetch) can read them at submit time. `previousRequiredRef` tracks each field's last
  // isRequired flag to detect optional→required flips when a ward is added.
  const customFieldItemsRef = useRef<ICustomFieldFormItem[]>([]);
  // EN-5192: mirrors of the ward-driven field configuration, so handleOnSubmitForm — declared
  // above the memos that compute them — reads the current values. Same reason as
  // customFieldItemsRef above.
  const standardShownRef = useRef<Record<string, boolean>>({});
  const standardRequiredRef = useRef<Record<string, boolean>>({});
  const previousRequiredRef = useRef<Record<string, boolean>>({});
  const containerRef = useRef(null);
  const sectionRef = useRef(null);

  const emailRef = useRef<HTMLDivElement>(null);
  const confirmEmailRef = useRef<HTMLDivElement>(null);
  const firstNameRef = useRef<HTMLDivElement>(null);
  const lastNameRef = useRef<HTMLDivElement>(null);
  const wardRef = useRef<HTMLDivElement>(null);
  const hospitalIDRef = useRef<HTMLDivElement>(null);
  const sexRef = useRef<HTMLDivElement>(null);
  const ethinicityRef = useRef<HTMLDivElement>(null);
  const heightRef = useRef<HTMLDivElement>(null);
  const phoneNumberRef = useRef<HTMLDivElement>(null);
  const devicesRef = useRef<HTMLDivElement>(null);
  const allergiesRef = useRef<HTMLDivElement>(null);
  const diagnosisRef = useRef<HTMLDivElement>(null);
  const additionalDetailsRef = useRef<HTMLDivElement>(null);

  const emailOptions: IOption[] = [
    { label: t("addPatient_setup_hasEmail"), value: "email", key: uuidv4() },
    { label: t("addPatient_setup_noEmail"), value: "noEmail", key: uuidv4() },
  ];

  const sexOptions: IOption[] = [
    { label: t("addPatient_sexOptions_female"), value: "f", key: uuidv4() },
    { label: t("addPatient_sexOptions_male"), value: "m", key: uuidv4() },
  ];

  const ethnicityOptions: IOption[] = [
    {
      label: t("addPatient_ethnicityOptions_white"),
      value: "white",
      key: uuidv4(),
    },
    {
      label: t("addPatient_ethnicityOptions_black"),
      value: "black",
      key: uuidv4(),
    },
    {
      label: t("addPatient_ethnicityOptions_asian"),
      value: "asian",
      key: uuidv4(),
    },
    {
      label: t("addPatient_ethnicityOptions_otherEthnicity"),
      value: "other",
      key: uuidv4(),
    },
  ];

  const verifyNoAppEnrolment = () => {
    const wardConfig = safeStorage.getItem(AUTH_WARD_CONFIGURATION);

    const parsedWardConfig = safeJsonParse<IWardConfiguration[] | undefined>(
      wardConfig,
      undefined
    );

    parsedWardConfig?.forEach((wardConfig) => {
      if (wardConfig.enableNoAppEnrolment) {
        setHasNoAppEnrolment(true);
      }
    });
  };

  useEffect(() => {
    disablePatientListArrowNav();

    setEnabledOnboardingFields(getEnabledOnboardingFields());

    const portalPreferences = safeStorage.getItem(AUTH_PORTAL_PREFERENCES);

    if (portalPreferences) {
      const parsedPortalPreferences = safeJsonParse<
        IPortalPreferences | undefined
      >(portalPreferences, undefined);

      if (parsedPortalPreferences) {
        setCountryCode(parsedPortalPreferences.countryCode);
      }
    }

    const preferences = safeStorage.getItem(AUTH_UNIT_PREFERENCES);

    const parsedPreferences = safeJsonParse<IUserPreferences | undefined>(
      preferences,
      undefined
    );

    if (parsedPreferences) {
      setHeightUnit(parsedPreferences.heightUnit);
    }
    verifyNoAppEnrolment();

    devicesService.getDeviceTypes().then((types) => {
      if (types === undefined) return;

      const tempTypes: IOption[] = [];

      types.data.forEach((type) => {
        tempTypes.push({
          label: type.name,
          value: type.id,
          key: uuidv4(),
        });
      });

      setDeviceTypesOptions(tempTypes);
    });
  }, []);

  useEffect(() => {
    if (containerRef.current) {
      (containerRef.current as HTMLDivElement).click();
    }
  }, [containerRef.current]);

  const checkIfAddDeviceIsAvailable = (wardIds: string[]) => {
    // EN-4960: device-on-enrolment is available if ANY of the selected wards enables it.
    const selectedWardIds = wardIds.map((id) => Number.parseInt(id, 10));
    const canAddDeviceForAnyWard = isWardFeatureEnabledForPatient(
      selectedWardIds,
      "enableAddDeviceOnEnrolment"
    );

    if (canAddDeviceForAnyWard) {
      setCanAddDevice(true);

      setTimeout(() => {
        if (devicesRef.current) {
          devicesRef.current.scrollIntoView({ behavior: "smooth" });
        }
      }, 1);
    } else {
      setCanAddDevice(false);
    }
  };

  // Everything that has to follow a ward-set change. Both call sites (the auto-selected single
  // ward and the multi-select) go through here so the device gate and the onboarding-field
  // configuration can never drift apart.
  const handleWardsChanged = (wardIds: string[]) => {
    checkIfAddDeviceIsAvailable(wardIds);
    setWardIdsForFieldConfig(wardIds.map((id) => Number.parseInt(id, 10)));
  };

  const handleOnSubmitForm = async ({
    email,
    firstName,
    lastName,
    dateOfBirth,
    sex,
    ethnicity,
    ward,
    hospitalId,
    phoneNumber,
    height,
    deviceList,
    allergySnomedIds,
    diagnosisSnomedIds,
    customFields,
  }: any) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    try {
      // EN-5067: validate the custom fields (UX only; server is the source of truth) against the
      // fields currently shown, then build the payload from those same fields.
      const customFieldAnswers = (customFields as CustomFieldAnswers) ?? {};
      const clientErrors = validateCustomFields(
        customFieldItemsRef.current,
        customFieldAnswers
      );
      if (Object.keys(clientErrors).length > 0) {
        setCustomFieldErrors(clientErrors);
        return;
      }
      setCustomFieldErrors({});

      // EN-5192: date of birth is not in the yup schema, so formik cannot block on it. Stop here
      // when the ward requires it and it is missing, or when what was typed is not a real date —
      // otherwise an invalid date reached the payload and was sent as null.
      if (
        getDateOfBirthError(
          dateOfBirth,
          standardShownRef.current.dateOfBirth,
          standardRequiredRef.current.dateOfBirth,
          t
        ) !== ""
      ) {
        return;
      }

      const customFieldsPayload = buildCustomFieldsPayload(
        customFieldItemsRef.current,
        customFieldAnswers
      );

      const devicesToAdd: IEnrolemntAddDevice[] = [];

      deviceList?.forEach((device: IEnrolemntAddDevice) => {
        if (
          device.deviceTypeId !== undefined &&
          device.deviceTypeId !== "" &&
          device.deviceId !== undefined &&
          device.deviceId !== ""
        ) {
          devicesToAdd.push(device);
        }
      });

      const onboardingFields: Record<string, unknown> = {};

      if (onboardingGates.allergies) {
        // Sent as strings — SNOMED conceptIds can exceed Number.MAX_SAFE_INTEGER
        // and would be corrupted if converted to a JS number.
        onboardingFields.allergySnomedIds =
          allergySnomedIds && allergySnomedIds.length > 0
            ? allergySnomedIds
            : undefined;
      }
      if (onboardingGates.diagnosis) {
        onboardingFields.diagnosisSnomedIds =
          diagnosisSnomedIds && diagnosisSnomedIds.length > 0
            ? diagnosisSnomedIds
            : undefined;
      }

      const dateOfBirthUtc = toDateOfBirthPayload(dateOfBirth);

      const patient = {
        email: hasEmailValue === "noEmail" ? null : email,
        firstName,
        lastName,
        // EN-5060: the portal never sends a condition — every patient enrols on the generic
        // 'general' condition; conditions are managed in Retool only.
        // EN-5192: a field the wards hide is never sent, even if it was filled in before the
        // ward set changed — the server would otherwise store a value the operator can no longer
        // see or correct.
        dateOfBirth: standardShownRef.current.dateOfBirth
          ? dateOfBirthUtc
          : null,
        sex: standardShownRef.current.sex ? sex : "",
        ethnicity: standardShownRef.current.ethnicity ? ethnicity : "",
        hospitalPatientId: standardShownRef.current.hospitalPatientId
          ? hospitalId
          : "",
        phoneNumber: standardShownRef.current.phoneNumber ? phoneNumber : "",
        // EN-4960: first selected ward is the primary; send the full set as wardIds.
        primaryWardId: parseInt(ward[0], 10),
        wardIds: (ward as string[]).map((w) => parseInt(w, 10)),
        height: standardShownRef.current.height ? height : "",
        devices: devicesToAdd,
        customFields: customFieldsPayload,
        ...onboardingFields,
      };

      setIsLoadingSubmit(true);

      const result = await patientService.addPatient(patient);

      if (result.status >= 200 && result.status < 300) {
        queryClient.invalidateQueries({
          queryKey: PATIENT_SEARCH_LIST_QUERY_KEY,
        });
        onClose({
          show: true,
          message: t("addPatient_toasts_added"),
          type: "success",
          width: "max-content",
          rightMargin: "25%",
        });
        return;
      }

      // EN-5067: map any CustomFieldValidationFailed errors back to their field by label prefix and
      // show them inline; everything else stays at the section-level response error.
      const errors = (result.data as any)?.errors as
        | ICustomFieldError[]
        | undefined;
      if (errors && errors.length > 0) {
        const customMessages = errors
          .filter((error) => error.code === "CustomFieldValidationFailed")
          .map((error) => error.message);
        const otherMessages = errors
          .filter((error) => error.code !== "CustomFieldValidationFailed")
          .map((error) => error.message);

        if (customMessages.length > 0) {
          const { fieldErrors, sectionErrors } = mapCustomFieldErrorsByLabel(
            customFieldItemsRef.current,
            customMessages
          );
          setCustomFieldErrors(fieldErrors);
          const remaining = [...sectionErrors, ...otherMessages];
          setResponseError(remaining.join(" "));
        } else {
          setResponseError(errors[0].message);
        }
      }
      setIsLoadingSubmit(false);
    } finally {
      isSubmittingRef.current = false;
    }
  };

  /* eslint-disable react/no-this-in-sfc */
  const deviceSchema = yup.object().shape({
    deviceId: yup
      .string()
      .test(
        "validate-deviceId",
        t("addPatient_validation_invalidIdentifierTitle"),
        function validateIdentifier(value) {
          const { deviceTypeId } = this.parent;

          if (deviceTypeId?.trim()) {
            if (!value?.trim()) {
              return this.createError({
                message: t("addPatient_validation_invalidIdentifier"),
              });
            }
            if (!/^\d+$/.test(value)) {
              return this.createError({
                message: t("addPatient_validation_invalidIdentifier"),
              });
            }
            if (value.length !== 15) {
              return this.createError({
                message: t("addPatient_validation_exactly15"),
              });
            }
          }

          return true;
        }
      ),

    deviceTypeId: yup
      .string()
      .test(
        "identifier-triggers-type",
        t("addPatient_validation_deviceTypeRequired"),
        function validateDeviceType(value) {
          const { deviceId } = this.parent;

          if (deviceId?.trim() && !value?.trim()) {
            return this.createError({
              message: t("addPatient_validation_deviceTypeRequired"),
            });
          }

          return true;
        }
      ),
  });
  /* eslint-enable react/no-this-in-sfc */

  // EN-5192: the ward decides which built-in fields are shown and which are mandatory. Only first
  // name, last name and email are unconditional. Defaults (all shown, none mandatory) apply until
  // the query resolves, so the form never blanks a field while a request is in flight.
  const { standardFields } = useStandardFieldsForWards(wardIdsForFieldConfig);

  const standardShown = useMemo(
    () => ({
      hospitalPatientId: isStandardFieldEnabled(
        "HospitalPatientId",
        standardFields
      ),
      dateOfBirth: isStandardFieldEnabled("DateOfBirth", standardFields),
      sex: isStandardFieldEnabled("Sex", standardFields),
      ethnicity: isStandardFieldEnabled("Ethnicity", standardFields),
      height: isStandardFieldEnabled("Height", standardFields),
      phoneNumber: isStandardFieldEnabled("PhoneNumber", standardFields),
    }),
    [standardFields]
  );

  const standardRequired = useMemo(
    () => ({
      hospitalPatientId: isStandardFieldRequired(
        "HospitalPatientId",
        standardFields
      ),
      dateOfBirth: isStandardFieldRequired("DateOfBirth", standardFields),
      sex: isStandardFieldRequired("Sex", standardFields),
      ethnicity: isStandardFieldRequired("Ethnicity", standardFields),
      height: isStandardFieldRequired("Height", standardFields),
      phoneNumber: isStandardFieldRequired("PhoneNumber", standardFields),
    }),
    [standardFields]
  );

  standardShownRef.current = standardShown;
  standardRequiredRef.current = standardRequired;

  const formValidationSchema = yup.object().shape({
    email: yup
      .string()
      .email(t("addPatient_validation_emailInvalid"))
      .required(t("addPatient_validation_emailInvalid")),
    confirmEmail: yup
      .string()
      .oneOf(
        [yup.ref("email"), null],
        t("addPatient_validation_emailsDoNotMatch")
      )
      .required(t("addPatient_validation_emailInvalid")),
    firstName: yup.string().required(t("addPatient_validation_nameRequired")),
    lastName: yup.string().required(t("addPatient_validation_nameRequired")),
    ward: yup
      .array()
      .of(yup.string())
      .min(1, t("addPatient_validation_valueRequired")),
    hospitalId: standardRequired.hospitalPatientId
      ? yup.string().required(t("addPatient_validation_hospitalIdRequired"))
      : yup.string().nullable().notRequired(),
    sex: standardRequired.sex
      ? yup.string().required(t("addPatient_validation_valueRequired"))
      : yup.string().nullable().notRequired(),
    ethnicity: standardRequired.ethnicity
      ? yup.string().required(t("addPatient_validation_valueRequired"))
      : yup.string().nullable().notRequired(),
    phoneNumber: standardRequired.phoneNumber
      ? yup.string().required(t("addPatient_validation_valueRequired"))
      : yup.string().nullable().notRequired(),
    height: (standardRequired.height
      ? yup.number().required(t("addPatient_validation_valueRequired"))
      : yup.number()
    ).test(
      "valid",
      t(
        heightUnit === "ft"
          ? "addPatient_validation_heightFeet"
          : "addPatient_validation_heightCm"
      ),
      (val) => {
        // EN-5192: a hidden field has no input to correct, so a height typed before the ward set
        // hid it must not block the submit with an error nothing renders — same rule as
        // getDateOfBirthError applies to the date of birth.
        if (!standardShown.height) return true;

        if (val === undefined) return true;

        if (heightUnit === "ft") {
          return val > 0 && val <= 8.8;
        }

        return val > 0 && val <= 270;
      }
    ),
    deviceList: yup.array().of(deviceSchema),
    allergySnomedIds: onboardingRequired.allergies
      ? yup.array().min(1, t("addPatient_validation_valueRequired"))
      : yup.array().notRequired(),
    diagnosisSnomedIds: onboardingRequired.diagnosis
      ? yup.array().min(1, t("addPatient_validation_valueRequired"))
      : yup.array().notRequired(),
  });

  const formik = useFormik({
    initialValues: {
      email: "",
      confirmEmail: "",
      firstName: "",
      lastName: "",
      ward: [] as string[],
      hospitalId: "",
      height: "",
      // EN-5272: `YYYY-MM-DD` from the picker, "" when unset.
      dateOfBirth: "",
      sex: "",
      ethnicity: "",
      phoneNumber: "",
      deviceList: [] as IEnrolemntAddDevice[],
      allergySnomedIds: [] as string[],
      diagnosisSnomedIds: [] as string[],
      // EN-5067: answers to the dynamic custom fields, keyed by customFieldDefinitionId so values
      // survive ward changes (cache-by-id).
      customFields: {} as CustomFieldAnswers,
    },
    onSubmit: handleOnSubmitForm,
    validationSchema: formValidationSchema,
  });

  // EN-5067: fetch the custom fields for the currently selected wards. Ward-driven — the query key
  // is the ward set, so it refetches whenever wards change and is empty when none are selected.
  const selectedWardIds = (formik.values.ward as string[]).map((w) =>
    parseInt(w, 10)
  );
  const { customFields: customFieldItems } =
    useCustomFieldsForWards(selectedWardIds);
  // Mirror into a ref so handleOnSubmitForm (declared before this fetch) can read the current set.
  customFieldItemsRef.current = customFieldItems;

  // Flag fields that flipped from optional to required after a ward was added this session.
  useEffect(() => {
    const previous = previousRequiredRef.current;
    const newly = customFieldItems
      .filter(
        (item) =>
          item.isRequired && previous[item.customFieldDefinitionId] === false
      )
      .map((item) => item.customFieldDefinitionId);
    // Only replace the state when the ids actually changed: this effect writes state from a value
    // in its own dep array, so handing React a new (even empty) array every time would re-render
    // in a loop and make every dropdown in the modal unselectable.
    setNewlyRequiredIds((previousIds) =>
      previousIds.length === newly.length &&
      previousIds.every((id, index) => id === newly[index])
        ? previousIds
        : newly
    );

    const next: Record<string, boolean> = {};
    customFieldItems.forEach((item) => {
      next[item.customFieldDefinitionId] = item.isRequired;
    });
    previousRequiredRef.current = next;
  }, [customFieldItems]);

  // EN-5067: one click validates the default fields AND the custom fields in the same pass, so
  // every error shows at once. The custom-field check used to live only inside onSubmit, which
  // formik reaches only after yup has already passed — so their errors surfaced a round later,
  // after the user had fixed the default fields.
  const handleSubmitClick = async () => {
    const answers = (formik.values.customFields as CustomFieldAnswers) ?? {};
    const customErrors = validateCustomFields(
      customFieldItemsRef.current,
      answers
    );
    setCustomFieldErrors(customErrors);

    const defaultFieldErrors = await formik.validateForm();

    // Runs yup again and marks every field touched, so the default-field messages appear.
    // handleOnSubmitForm re-checks the custom fields and stops the request if any is invalid.
    await formik.submitForm();

    // Only the custom fields are wrong: nothing above scrolls into view, so point at the section.
    if (
      Object.keys(defaultFieldErrors).length === 0 &&
      Object.keys(customErrors).length > 0
    ) {
      additionalDetailsRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleCustomFieldChange = (
    definitionId: string,
    answer: ICustomFieldAnswer
  ) => {
    formik.setFieldValue("customFields", {
      ...((formik.values.customFields as CustomFieldAnswers) ?? {}),
      [definitionId]: answer,
    });
    // Clear any inline error for the field being edited.
    setCustomFieldErrors((previous) => {
      if (!previous[definitionId]) return previous;
      const next = { ...previous };
      delete next[definitionId];
      return next;
    });
  };

  useEffect(() => {
    if (!isWardsFetching && !isWardsError) {
      if (availableWards.length > 1) {
        setShowWards(true);

        availableWards.forEach((ward) => {
          wardOptions.push({
            label: ward.wardName,
            value: ward.wardId.toString(),
            key: uuidv4(),
          });
        });
      } else if (availableWards.length === 1) {
        const onlyWard = availableWards[0].wardId.toString();
        formik.setFieldValue("ward", [onlyWard]);
        handleWardsChanged([onlyWard]);
      }
    }
  }, [isWardsFetching]);

  useEffect(() => {
    if (!isWardsFetching && !isWardsError) {
      if (hasEmailValue !== "noEmail") {
        const myInput = document.getElementsByName(
          "confirmEmail"
        )[0] as HTMLInputElement;
        myInput.onpaste = (e) => e.preventDefault();
        formik.setFieldValue("email", "");
        formik.setFieldValue("confirmEmail", "");
      } else {
        formik.setFieldValue("email", "dummy@pmp.pt");
        formik.setFieldValue("confirmEmail", "dummy@pmp.pt");
      }
    }
  }, [hasEmailValue, isWardsFetching]);

  useEffect(() => {
    formik.setFieldValue("allergySnomedIds", []);
    formik.setFieldValue("diagnosisSnomedIds", []);
    formik.setFieldTouched("allergySnomedIds", false, false);
    formik.setFieldTouched("diagnosisSnomedIds", false, false);
    setAllergiesSelected([]);
    setDiagnosesSelected([]);
  }, [enabledOnboardingFields]);

  const shouldShowErrorMessage = (fieldPath: string): string => {
    const touched = getIn(formik.touched, fieldPath);
    const error = getIn(formik.errors, fieldPath);

    return touched ? error ?? "" : "";
  };

  const shouldShowDateErrorMessage = () =>
    formik.touched.dateOfBirth
      ? getDateOfBirthError(
          formik.values.dateOfBirth,
          standardShown.dateOfBirth,
          standardRequired.dateOfBirth,
          t
        )
      : "";

  const errorFieldRefs: Record<
    string,
    React.RefObject<HTMLDivElement> | undefined
  > = {
    email: emailRef,
    confirmEmail: confirmEmailRef,
    firstName: firstNameRef,
    lastName: lastNameRef,
    ward: wardRef,
    hospitalId: hospitalIDRef,
    sex: sexRef,
    phoneNumber: phoneNumberRef,
    height: heightRef,
    deviceList: devicesRef,
    allergySnomedIds: allergiesRef,
    diagnosisSnomedIds: diagnosisRef,
  };

  const scrollToFirstError = () => {
    const errorFields = Object.keys(formik.errors);

    if (errorFields.length === 0) return;

    const errorRef = errorFieldRefs[errorFields[0]];

    if (errorRef?.current) {
      errorRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    if (formik.errors && Object.keys(formik.errors).length > 0) {
      scrollToFirstError();
    }
  }, [formik.isSubmitting]);

  const handleCountryChange = () => {
    const inputElement = document.getElementById("phone-input");
    if (inputElement) {
      inputElement.focus();
    }
  };

  const handlePhoneInput = (country: any, formattedValue: string) => {
    const formattedPhoneNumber = formattedValue.replace(/ /g, "");
    const formattedDialCode = `+${country.dialCode}`;

    if (formattedPhoneNumber !== formattedDialCode) {
      formik.setFieldValue("phoneNumber", formattedPhoneNumber);
    } else {
      formik.setFieldValue("phoneNumber", "");
    }

    setPhoneWithCountryCode(formattedValue);
  };

  const handleAddDevice = () => {
    const newDevice: IEnrolemntAddDevice = {
      id: uuidv4(),
      deviceTypeId: undefined,
      deviceId: undefined,
    };

    formik.setFieldValue("deviceList", [
      ...formik.values.deviceList,
      newDevice,
    ]);
  };

  const handleRemoveDevice = (index: number) => {
    const updatedDevices = formik.values.deviceList.filter(
      (_, i) => i !== index
    );
    formik.setFieldValue("deviceList", updatedDevices);
  };

  return (
    <ModalContainer style={{ height: "100%" }} ref={containerRef}>
      <Title>{t("addPatient_title")}</Title>
      <br />

      {!isWardsFetching ? (
        <EnrolmentContainer>
          <SectionContainer ref={sectionRef}>
            {hasNoAppEnrolment && (
              <FormField>
                <p>{t("addPatient_setup_label")}</p>
                <SelectDropdown
                  popUpCsss={dropdownPopUpCss()}
                  className={dropdownCss()}
                  options={emailOptions}
                  width={410}
                  value={hasEmailValue}
                  placeholder={t("addPatient_setup_hasEmail")}
                  onValueChange={(value: string | string[]) => {
                    setHasEmailValue(value.toString());
                  }}
                />
              </FormField>
            )}
            {hasEmailValue !== "noEmail" && (
              <>
                <FormField ref={emailRef}>
                  <p>{t("addPatient_fields_emailAddress")} *</p>
                  <TextField
                    autofocus
                    label=""
                    name="email"
                    placeholder="jane@example.com"
                    value={formik.values.email}
                    onChange={(event) =>
                      formik.setFieldValue(
                        "email",
                        event.target.value.toLowerCase()
                      )
                    }
                    backgroudColor="white"
                    className={inputCss()}
                    errorMessage={shouldShowErrorMessage("email")}
                  />
                </FormField>

                <FormField ref={confirmEmailRef}>
                  <p style={{ whiteSpace: "nowrap" }}>
                    {t("addPatient_fields_confirmEmail")} *
                  </p>
                  <TextField
                    label=""
                    name="confirmEmail"
                    placeholder={t("addPatient_placeholders_confirmEmail")}
                    value={formik.values.confirmEmail}
                    onChange={(event) =>
                      formik.setFieldValue(
                        "confirmEmail",
                        event.target.value.toLowerCase()
                      )
                    }
                    backgroudColor="white"
                    className={inputCss()}
                    errorMessage={shouldShowErrorMessage("confirmEmail")}
                  />
                </FormField>
              </>
            )}

            <FormField ref={firstNameRef}>
              <p>{t("addPatient_fields_firstName")} *</p>
              <TextField
                label=""
                name="firstName"
                placeholder={t("addPatient_placeholders_firstName")}
                value={formik.values.firstName}
                onChange={formik.handleChange}
                backgroudColor="white"
                className={inputCss()}
                errorMessage={shouldShowErrorMessage("firstName")}
              />
            </FormField>

            <FormField ref={lastNameRef}>
              <p>{t("addPatient_fields_lastName")} *</p>
              <TextField
                label=""
                name="lastName"
                placeholder={t("addPatient_placeholders_lastName")}
                value={formik.values.lastName}
                onChange={formik.handleChange}
                backgroudColor="white"
                className={inputCss()}
                errorMessage={shouldShowErrorMessage("lastName")}
              />
            </FormField>

            {standardShown.hospitalPatientId && (
              <FormField ref={hospitalIDRef}>
                <p>
                  {t("addPatient_fields_hospitalId")}
                  {requiredMarker(standardRequired.hospitalPatientId)}
                </p>
                <TextField
                  className={inputCss()}
                  label=""
                  name="hospitalId"
                  placeholder={t("addPatient_placeholders_hospitalId")}
                  value={formik.values.hospitalId}
                  onChange={formik.handleChange}
                  backgroudColor="white"
                  errorMessage={shouldShowErrorMessage("hospitalId")}
                  auxiliarButtonLabel={t("addPatient_clickButton")}
                />
              </FormField>
            )}

            {standardShown.dateOfBirth && (
              <FormField>
                <p>
                  {t("addPatient_fields_dateOfBirth")}
                  {requiredMarker(standardRequired.dateOfBirth)}
                </p>
                <DateTimePickerInput
                  showTime={false}
                  format={getDateFormatForCountry(countryCode)}
                  placeholder={getDateFormatForCountry(countryCode)}
                  // The width of the Add patient fields around it.
                  width="248px"
                  maxDate={dayjs()}
                  value={
                    formik.values.dateOfBirth
                      ? dayjs(formik.values.dateOfBirth, DATE_VALUE_FORMAT)
                      : null
                  }
                  // Clearing hands back null despite the signature, hence the guard.
                  onChange={(next: Dayjs | null) => {
                    formik.setFieldTouched("dateOfBirth", true, false);
                    formik.setFieldValue(
                      "dateOfBirth",
                      next?.isValid() ? next.format(DATE_VALUE_FORMAT) : ""
                    );
                  }}
                />
                <ErrorMessage>{shouldShowDateErrorMessage()}</ErrorMessage>
              </FormField>
            )}

            {standardShown.sex && (
              <FormField ref={sexRef}>
                <p>
                  {t("addPatient_fields_sex")}
                  {requiredMarker(standardRequired.sex)}
                </p>
                <SelectDropdown
                  popUpCsss={dropdownPopUpCss()}
                  className={dropdownCss()}
                  options={sexOptions}
                  width={410}
                  placeholder={t("addPatient_placeholders_chooseSex")}
                  value={sexSelected}
                  onValueChange={(value: string | string[]) => {
                    setSexSelected(value.toString());
                    formik.setFieldValue("sex", value.toString());
                  }}
                />
                <ErrorMessage>{shouldShowErrorMessage("sex")}</ErrorMessage>
              </FormField>
            )}

            {standardShown.ethnicity && (
              <FormField ref={ethinicityRef}>
                <p>
                  {t("addPatient_fields_ethnicity")}
                  {requiredMarker(standardRequired.ethnicity)}
                </p>
                <SelectDropdown
                  popUpCsss={dropdownPopUpCss()}
                  className={dropdownCss()}
                  options={ethnicityOptions}
                  width={410}
                  placeholder={t("addPatient_placeholders_chooseEthnicity")}
                  value={ethnicitySelected}
                  onValueChange={(value: string | string[]) => {
                    setEthnicitySelected(value.toString());
                    formik.setFieldValue("ethnicity", value.toString());
                  }}
                />
                <ErrorMessage>
                  {shouldShowErrorMessage("ethnicity")}
                </ErrorMessage>
              </FormField>
            )}

            {showWards && (
              <FormField ref={wardRef}>
                <p>
                  {t("addPatient_fields_selectWards", {
                    ward: wardLabel.pluralLower,
                  })}{" "}
                  *
                </p>
                <SelectDropdown
                  multiple
                  popUpCsss={dropdownPopUpCss()}
                  className={dropdownCss()}
                  options={wardOptions}
                  width={410}
                  placeholder={t("addPatient_placeholders_chooseWards", {
                    ward: wardLabel.pluralLower,
                  })}
                  value={formik.values.ward}
                  onValueChange={(value: string | string[]) => {
                    const wards = Array.isArray(value) ? value : [value];
                    formik.setFieldValue("ward", wards);

                    handleWardsChanged(wards);
                  }}
                />
                <ErrorMessage>{shouldShowErrorMessage("ward")}</ErrorMessage>
              </FormField>
            )}

            {standardShown.height && (
              <FormField ref={heightRef}>
                <p>
                  {t("addPatient_fields_height")}
                  {requiredMarker(standardRequired.height)}
                </p>
                <TextField
                  label=""
                  name="height"
                  type="number"
                  placeholder={t("addPatient_placeholders_heightUnit", {
                    unit: heightUnit,
                  })}
                  value={formik.values.height}
                  onChange={formik.handleChange}
                  onKeyDown={(evt) =>
                    ["e", "E", "+", "-"].includes(evt.key) &&
                    evt.preventDefault()
                  }
                  backgroudColor="white"
                  className={inputCss()}
                  errorMessage={shouldShowErrorMessage("height")}
                />
              </FormField>
            )}

            {standardShown.phoneNumber && (
              <FormField ref={phoneNumberRef}>
                <p>
                  {t("addPatient_fields_phoneNumber")}
                  {requiredMarker(standardRequired.phoneNumber)}
                </p>

                <PhoneInput
                  inputProps={{
                    id: "phone-input",
                  }}
                  searchPlaceholder={t("addPatient_placeholders_search")}
                  country={countryCode?.toLowerCase()}
                  containerClass={reactPhoneInputCss()}
                  enableSearch
                  value={phoneWithCountryCode}
                  onChange={(value, country, e, formattedValue) => {
                    handleCountryChange();
                    handlePhoneInput(country, formattedValue);
                  }}
                  countryCodeEditable={false}
                />
                <ErrorMessage>
                  {shouldShowErrorMessage("phoneNumber")}
                </ErrorMessage>
              </FormField>
            )}

            {onboardingGates.allergies && (
              <FormField ref={allergiesRef}>
                <p>
                  {t("addPatient_fields_allergies")}
                  {onboardingRequired.allergies ? " *" : ""}
                </p>
                <SnomedSelect
                  value={allergiesSelected}
                  onChange={(selected) => {
                    setAllergiesSelected(selected);
                    formik.setFieldValue(
                      "allergySnomedIds",
                      selected.map((option) => option.value)
                    );
                  }}
                  searchOptions={searchAllergyOptions}
                  queryKey="allergies"
                  modalTitle={t("addPatient_modals_addAllergies")}
                  placeholder={t("addPatient_placeholders_selectAllergies")}
                  triggerWidth="248px"
                  searchPlaceholder={t("addPatient_modals_searchAllergies")}
                  hasError={Boolean(shouldShowErrorMessage("allergySnomedIds"))}
                />
                <ErrorMessage>
                  {shouldShowErrorMessage("allergySnomedIds")}
                </ErrorMessage>
              </FormField>
            )}

            {onboardingGates.diagnosis && (
              <FormField ref={diagnosisRef}>
                <p>
                  {t("addPatient_fields_diagnosis")}
                  {onboardingRequired.diagnosis ? " *" : ""}
                </p>
                <SnomedSelect
                  value={diagnosesSelected}
                  onChange={(selected) => {
                    setDiagnosesSelected(selected);
                    formik.setFieldValue(
                      "diagnosisSnomedIds",
                      selected.map((option) => option.value)
                    );
                  }}
                  searchOptions={searchDiagnosisOptions}
                  queryKey="diagnoses"
                  modalTitle={t("addPatient_modals_addDiagnosis")}
                  placeholder={t("addPatient_placeholders_selectDiagnosis")}
                  triggerWidth="248px"
                  searchPlaceholder={t("addPatient_modals_searchDiagnosis")}
                  hasError={Boolean(
                    shouldShowErrorMessage("diagnosisSnomedIds")
                  )}
                />
                <ErrorMessage>
                  {shouldShowErrorMessage("diagnosisSnomedIds")}
                </ErrorMessage>
              </FormField>
            )}
          </SectionContainer>

          {/* EN-5067: dynamic per-ward custom fields; hidden when the selected wards define none. */}
          <div ref={additionalDetailsRef}>
            <AdditionalDetailsSection
              items={customFieldItems}
              answers={formik.values.customFields as CustomFieldAnswers}
              errors={customFieldErrors}
              newlyRequiredIds={newlyRequiredIds}
              countryCode={countryCode}
              onChange={handleCustomFieldChange}
            />
          </div>

          {canAddDevice && (
            <div ref={devicesRef}>
              {formik.values.deviceList && formik.values.deviceList.length > 0
                ? formik.values.deviceList.map((device, index) => (
                    <SectionContainer isDevice key={device.id}>
                      <FormField isDevice>
                        <p>{t("addPatient_fields_deviceType")}</p>
                        <SelectDropdown
                          popUpCsss={dropdownPopUpCss()}
                          className={dropdownCss()}
                          options={deviceTypesOptions}
                          width={410}
                          value={formik.values.deviceList[index].deviceTypeId}
                          placeholder={t("addPatient_placeholders_deviceType")}
                          onValueChange={(value: string | string[]) => {
                            formik.setFieldValue(
                              `deviceList[${index}].deviceTypeId`,
                              value.toString()
                            );
                          }}
                        />
                        <ErrorMessage isDevice>
                          {shouldShowErrorMessage(
                            `deviceList[${index}].deviceTypeId`
                          )}
                        </ErrorMessage>
                      </FormField>
                      <FormField isDevice>
                        <p>{t("addPatient_fields_deviceId")}</p>
                        <TextField
                          label=""
                          name={`deviceList[${index}].deviceId`}
                          type="text"
                          placeholder={t("addPatient_placeholders_deviceId")}
                          value={formik.values.deviceList[index].deviceId}
                          onChange={formik.handleChange}
                          onKeyDown={(evt) =>
                            ["e", "E", "+", "-"].includes(evt.key) &&
                            evt.preventDefault()
                          }
                          backgroudColor="white"
                          className={inputCss()}
                          errorMessage={shouldShowErrorMessage(
                            `deviceList[${index}].deviceId`
                          )}
                        />
                      </FormField>

                      <CloseContainer onClick={() => handleRemoveDevice(index)}>
                        <Close />
                      </CloseContainer>
                    </SectionContainer>
                  ))
                : null}

              <AddDeviceButton onClick={handleAddDevice}>
                <span>+</span> {t("addPatient_addDevice")}
              </AddDeviceButton>
            </div>
          )}
        </EnrolmentContainer>
      ) : (
        <SkeletonLoading height="432px" />
      )}

      <ErrorMessage isResponseError>{responseError}</ErrorMessage>

      <SubmitContainer>
        <Button
          label={
            hasEmailValue === "noEmail"
              ? t("addPatient_save")
              : t("addPatient_saveAndSendEmail")
          }
          type="submit"
          onClick={handleSubmitClick}
          isLoading={isLoadingSubmit}
        />

        <CancelButton onClick={() => onClose()} style={{ marginLeft: "-30px" }}>
          {t("common_cancel")}
        </CancelButton>
      </SubmitContainer>
    </ModalContainer>
  );
}
